// Reusable conversation-outline timeline. Renders the active session as a
// vertical timeline / stepper: one card per Q/A turn, joined by a single
// continuous track with a dot centered on each card. The question and the
// answer are separate jump targets. This content is shared by the mobile
// `MinimapBottomSheet` (< xl) and the persistent right dock (>= xl).
//
// Closing/dismissing is NOT the timeline's concern: callers pass an `onJumpTo`
// that, in the sheet, also closes the sheet; in the dock, it just scrolls.

import { type JSX, useLayoutEffect, useRef } from "react";

import { cn } from "@/lib/utils";

import { type MinimapItem, groupIntoTurns, heuristicLabel } from "./Minimap";

interface MinimapTimelineProps {
  items: MinimapItem[];
  /** Id of the message marking the active turn (highlighted). */
  activeItemId?: string | null;
  onJumpTo: (itemId: string) => void;
  /**
   * When set, on mount the timeline pins its nearest scrollable ancestor to the
   * bottom (latest turn). Used by the sheet, which opens at the live end of the
   * conversation. The persistent dock leaves this unset.
   */
  pinToBottomOnMount?: boolean;
}

function labelFor(item: MinimapItem): string {
  if (item.minimap_label && item.minimap_label.trim().length > 0) {
    return item.minimap_label;
  }
  return heuristicLabel(item.content);
}

/** Locale/timezone-aware short clock (e.g. "10:12 AM" / "10:12"). Returns
 * `null` for missing or unparseable timestamps so the row simply omits it. */
function formatTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export function MinimapTimeline({
  items,
  activeItemId,
  onJumpTo,
  pinToBottomOnMount,
}: MinimapTimelineProps): JSX.Element {
  const turns = groupIntoTurns(items);

  // Pin the nearest scrollable ancestor to the bottom on mount (sheet only).
  // Walk up from the bottom anchor to the first genuinely scrollable ancestor.
  const endRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!pinToBottomOnMount) return;
    let el = endRef.current?.parentElement ?? null;
    while (el) {
      const overflowY = getComputedStyle(el).overflowY;
      if ((overflowY === "auto" || overflowY === "scroll") && el.scrollHeight > el.clientHeight) {
        el.scrollTop = el.scrollHeight;
        return;
      }
      el = el.parentElement;
    }
  }, [pinToBottomOnMount]);

  // Clamp the timeline track to run from the first dot's center to the last
  // dot's center (no overhang past the endpoints). Card heights vary, so the
  // geometry is measured rather than expressed in static offsets. Recomputed
  // when the turn count changes (e.g. a live conversation grows).
  const lineRef = useRef<HTMLDivElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `turns.length` is a deliberate re-measure trigger, not a value read inside the effect
  useLayoutEffect(() => {
    const line = lineRef.current;
    const wrapper = line?.parentElement;
    if (!line || !wrapper) return;
    const measure = (): void => {
      const dots = [...wrapper.querySelectorAll('[data-testid="sheet-turn"]')].map(
        (row) => row.firstElementChild,
      );
      const firstDot = dots[0];
      const lastDot = dots[dots.length - 1];
      if (dots.length < 2 || !firstDot || !lastDot) {
        line.style.display = "none";
        return;
      }
      const wrapTop = wrapper.getBoundingClientRect().top;
      const first = firstDot.getBoundingClientRect();
      const last = lastDot.getBoundingClientRect();
      const top = first.top + first.height / 2 - wrapTop;
      const bottom = last.top + last.height / 2 - wrapTop;
      line.style.display = "";
      line.style.top = `${top}px`;
      line.style.height = `${bottom - top}px`;
    };
    measure();
    // Re-measure when a card's height changes without the turn count changing
    // (an async minimap_label swaps in, or a streaming answer grows) so the
    // track keeps spanning exactly from the first dot's center to the last.
    const observer = new ResizeObserver(measure);
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [turns.length]);

  if (turns.length === 0) {
    return <p className="text-body-3 text-muted-foreground">Sin mensajes en esta sesión.</p>;
  }

  return (
    <div className="relative min-h-full">
      {/* Continuous track behind the dots; top/height set imperatively above. */}
      <div
        ref={lineRef}
        aria-hidden="true"
        className="absolute left-1.5 w-px -translate-x-1/2 bg-border"
      />
      <div className="space-y-3">
        {turns.map((turn) => {
          // First item opens the turn = the question. The answer is the
          // assistant reply (absent while a turn is still streaming).
          const question = turn[0];
          if (!question) return null;
          const answer = turn.find((m) => m.role === "assistant");
          const isActive = activeItemId != null && turn.some((m) => m.id === activeItemId);
          const time = formatTime(turn.find((m) => m.created_at)?.created_at);
          return (
            <div
              key={turn.map((m) => m.id).join(":")}
              data-testid="sheet-turn"
              data-active={isActive ? "true" : "false"}
              className="relative flex w-full items-center gap-3"
            >
              {/* Timeline dot, vertically centered on the card. */}
              <span
                className={cn(
                  "relative z-10 size-3 shrink-0 rounded-full border-2 transition-colors",
                  isActive
                    ? "border-brand-extra-2-40 bg-brand-extra-2-40 ring-2 ring-brand-extra-2-40/30"
                    : "border-muted-foreground/50 bg-card",
                )}
              />
              <div
                className={cn(
                  "min-w-0 flex-1 rounded-lg border px-3 py-2 transition-colors",
                  isActive ? "border-ring/60 bg-secondary/10" : "border-border bg-muted/30",
                )}
              >
                {/* Question (clickable) with the turn time pinned top-right. */}
                <div className="flex items-start justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => onJumpTo(question.id)}
                    className="-my-1 min-w-0 flex-1 rounded-md py-1 text-left transition-colors hover:bg-muted"
                  >
                    <span className="line-clamp-2 block text-body-3 font-semibold text-foreground">
                      {labelFor(question)}
                    </span>
                  </button>
                  {time ? (
                    <span className="shrink-0 pt-0.5 text-label-1 text-muted-foreground">
                      {time}
                    </span>
                  ) : null}
                </div>
                {/* Answer (clickable), split from the question by a thin rule. */}
                {answer ? (
                  <>
                    <div className="my-2 h-px w-3 bg-border" />
                    <button
                      type="button"
                      onClick={() => onJumpTo(answer.id)}
                      className="-my-1 block w-full rounded-md py-1 text-left text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    >
                      <span className="line-clamp-2 block text-body-3">{labelFor(answer)}</span>
                    </button>
                  </>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
      {/* Bottom anchor: pin-to-bottom walks up from here on mount (sheet). */}
      <div ref={endRef} aria-hidden="true" />
    </div>
  );
}
